<p-toast></p-toast>

<app-page-shell>

    <app-page-detail-hero [auditStamp]="mobilization ? { createdAt: mobilization.createdAt, updatedAt: mobilization.updatedAt } : null"
        icon="pi pi-truck"
        eyebrow="Mobilization Detail"
        [title]="mobilization?.mobilizationNumber || ('MOB-' + (mobilization?.mobilizationId || ''))"
        [subtitle]="projectLabel() + ' | ' + siteLabel()">
        <div tags>
            <p-tag [value]="mobilization?.status || 'DRAFT'" [severity]="statusSeverity(mobilization?.status)"></p-tag>
            <p-tag [value]="projectSourceLabel(mobilization?.projectSource)" severity="info"></p-tag>
            <p-tag *ngIf="mobilization?.logisticsProjectId" [value]="'Project #' + mobilization?.logisticsProjectId" severity="contrast"></p-tag>
        </div>
        <div actions>
            <p-button label="Back" icon="pi pi-arrow-left" severity="secondary" [outlined]="true" routerLink="/fleet/mobilization/entries"></p-button>
            <p-button *ngIf="canSubmit()" label="Edit" icon="pi pi-pencil" severity="secondary" [outlined]="true" (onClick)="edit()"></p-button>
            <p-button label="Recalculate Salary" icon="pi pi-refresh" severity="secondary" [outlined]="true" [loading]="isActing" (onClick)="recalculateSalary()"></p-button>
            <p-button *ngIf="canSubmit()" label="Submit" icon="pi pi-send" [loading]="isActing" (onClick)="submit()"></p-button>
            <p-button *ngIf="canApproveReject()" label="Reject" icon="pi pi-times" severity="danger" [outlined]="true" [loading]="isActing" (onClick)="reject()"></p-button>
            <p-button *ngIf="canApproveReject()" label="Approve" icon="pi pi-check" [loading]="isActing" (onClick)="approve()"></p-button>
            <p-button *ngIf="canActivate()" label="Activate" icon="pi pi-play" [loading]="isActing" (onClick)="activate()"></p-button>
            <p-button *ngIf="canComplete()" label="Complete" icon="pi pi-flag" [loading]="isActing" (onClick)="complete()"></p-button>
            <p-button *ngIf="canCancel()" label="Cancel" icon="pi pi-ban" severity="danger" [outlined]="true" [loading]="isActing" (onClick)="cancel()"></p-button>
        </div>
    </app-page-detail-hero>

    <app-page-metric-strip [metrics]="metrics()"></app-page-metric-strip>

    <div class="detail-grid">
        <app-page-data-panel title="Assignment" subtitle="Driver, vehicle, and planned schedule snapshots.">
            <div class="info-grid">
                <div>
                    <span>Driver</span>
                    <strong>{{ mobilization?.driverName || '-' }}</strong>
                    <small>{{ mobilization?.driverPhoneNumber || mobilization?.driverLicenseNumber || '-' }}</small>
                </div>
                <div>
                    <span>Truck</span>
                    <strong>{{ mobilization?.vehiclePlateNumber || '-' }}</strong>
                    <small>{{ mobilization?.vehicleRegistrationNumber || mobilization?.vehicleTypeName || '-' }}</small>
                </div>
                <div>
                    <span>Trailer</span>
                    <strong>{{ trailerLabel() }}</strong>
                    <small>{{ mobilization?.trailerTypeName || mobilization?.trailerRegistrationNumber || '-' }}</small>
                </div>
                <div>
                    <span>Start Date</span>
                    <strong>{{ dateLabel(mobilization?.startDate) }}</strong>
                </div>
                <div>
                    <span>End Date</span>
                    <strong>{{ dateLabel(mobilization?.endDate) }}</strong>
                </div>
                <div>
                    <span>Expected Hours</span>
                    <strong>{{ mobilization?.expectedHoursPerWeek || '-' }}</strong>
                </div>
                <div>
                    <span>Assignment Role</span>
                    <strong>{{ mobilization?.roleName || '-' }}</strong>
                    <small>{{ supervisorLabel() }}</small>
                </div>
            </div>
        </app-page-data-panel>

        <app-page-data-panel title="Project and Site" subtitle="Logistics project or manually entered project/site details.">
            <div class="info-grid">
                <div>
                    <span>Project Source</span>
                    <strong>{{ projectSourceLabel(mobilization?.projectSource) }}</strong>
                </div>
                <div>
                    <span>Logistics Project</span>
                    <strong>{{ mobilization?.logisticsProjectId ? ('Project #' + mobilization?.logisticsProjectId) : 'Unassigned' }}</strong>
                    <small *ngIf="mobilization?.logisticsProjectId">
                        <button pButton pRipple type="button" label="Open Project" icon="pi pi-sitemap" class="p-button-text p-button-sm" (click)="openLogisticsProject()"></button>
                    </small>
                </div>
                <div>
                    <span>Project</span>
                    <strong>{{ projectLabel() }}</strong>
                </div>
                <div>
                    <span>Site</span>
                    <strong>{{ siteLabel() }}</strong>
                    <small>{{ mobilization?.manualSiteAddress || '-' }}</small>
                </div>
            </div>
        </app-page-data-panel>
    </div>

    <app-page-data-panel title="Salary Snapshot" subtitle="Backend-calculated salary values stored on this mobilization.">
        <div class="info-grid">
            <div>
                <span>Calculation Status</span>
                <strong>{{ mobilization?.salaryCalculationStatus || '-' }}</strong>
            </div>
            <div>
                <span>Daily Rate</span>
                <strong>{{ mobilization?.salaryDailyRate ?? '-' }} {{ mobilization?.salaryCurrencyCode || 'SAR' }}</strong>
            </div>
            <div>
                <span>Day Count</span>
                <strong>{{ mobilization?.salaryDayCount ?? '-' }}</strong>
            </div>
            <div>
                <span>Base Amount</span>
                <strong>{{ mobilization?.salaryBaseAmount ?? '-' }} {{ mobilization?.salaryCurrencyCode || 'SAR' }}</strong>
            </div>
            <div>
                <span>Manual Adjustment</span>
                <strong>{{ mobilization?.salaryManualAdjustment ?? 0 }} {{ mobilization?.salaryCurrencyCode || 'SAR' }}</strong>
            </div>
            <div>
                <span>Total Amount</span>
                <strong>{{ salaryLabel() }}</strong>
            </div>
            <div>
                <span>Rule</span>
                <strong>{{ mobilization?.salaryRuleId || '-' }}</strong>
            </div>
            <div>
                <span>Calculated At</span>
                <strong>{{ dateTimeLabel(mobilization?.salaryCalculatedAt) }}</strong>
            </div>
        </div>
    </app-page-data-panel>

    <app-page-data-panel *ngIf="rentalCostPreview" title="Rental Cost Preview" subtitle="Read-only daily-rental estimate. It does not create usage days or supplier payables.">
        <div class="info-grid">
            <div><span>Estimated rental cost</span><strong>{{ rentalCostLabel() }}</strong></div>
            <div><span>Calendar days</span><strong>{{ rentalCostPreview.calendarDays || 0 }}</strong></div>
            <div><span>Uncovered days</span><strong>{{ rentalCostPreview.uncoveredDays || 0 }}</strong></div>
            <div><span>Missing-rate days</span><strong>{{ rentalCostPreview.missingRateDays || 0 }}</strong></div>
        </div>
        <div class="notes-stack" *ngIf="rentalCostPreview.lines?.length">
            <div *ngFor="let line of rentalCostPreview.lines"><span>{{ line.resourceType }} {{ line.agreementNumber ? ('• ' + line.agreementNumber) : '' }}</span><p>{{ line.status }} — {{ line.message }}</p></div>
        </div>
    </app-page-data-panel>

    <div class="detail-grid">
        <app-page-data-panel title="Compliance and Notes" subtitle="Operational notes stored on this assignment.">
            <div class="notes-stack">
                <div>
                    <span>Shift Notes</span>
                    <p>{{ mobilization?.shiftNotes || 'No shift notes.' }}</p>
                </div>
                <div>
                    <span>General Notes</span>
                    <p>{{ mobilization?.notes || 'No assignment notes.' }}</p>
                </div>
                <div>
                    <span>Approval Comment</span>
                    <p>{{ mobilization?.approvalComment || 'No approval comment.' }}</p>
                </div>
            </div>
        </app-page-data-panel>

        <app-page-data-panel title="Audit Timeline" subtitle="Lifecycle events written by the backend.">
            <p-timeline [value]="mobilization?.auditEvents || []" align="left">
                <ng-template pTemplate="content" let-event>
                    <div class="timeline-event">
                        <div class="timeline-title">
                            <strong>{{ actionLabel(event.actionCode) || 'Updated' }}</strong>
                            <p-tag [value]="event.toStatus || event.fromStatus || 'EVENT'" [severity]="auditSeverity(event)"></p-tag>
                        </div>
                        <span>{{ dateTimeLabel(event.eventTime) }} by {{ event.actorName || 'System' }}</span>
                        <p>{{ event.comment || 'No comment.' }}</p>
                    </div>
                </ng-template>
            </p-timeline>
            <div *ngIf="!mobilization?.auditEvents?.length" class="empty-state">No audit events recorded yet.</div>
        </app-page-data-panel>
    </div>
</app-page-shell>
